import { CardGiftcard as GiftIcon } from "@mui/icons-material";
import Logger from "../../services/Logger";
import { Box, Button, Card, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/AuthService";
import ReferralService from "../../services/ReferralService";

export default function ReferralEventMiniSection() {
    const navigate = useNavigate();
    const [activeEvent, setActiveEvent] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);
            } catch (error) {
                Logger.error('Error checking authentication:', error);
                setIsAuthenticated(false);
            }
        };

        checkAuth();
    }, []);

    useEffect(() => {
        const loadActiveEvent = async () => {
            try {
                const event = await ReferralService.getActiveEvent();
                if (event && event.id) {
                    setActiveEvent(event);
                } else {
                    setActiveEvent(null);
                }
            } catch (error) {
                Logger.error('Error fetching active referral event:', error);
                setActiveEvent(null);
            }
        };

        loadActiveEvent();
    }, []);

    const formatDateRange = (startAt, endAt) => {
        if (!startAt || !endAt) return null;

        const startDate = moment(startAt);
        const endDate = moment(endAt);

        const getOrdinalSuffix = (day) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const startDay = startDate.date();
        const endDay = endDate.date();
        const startFormat = `${startDate.format("MMM")} ${startDay}${getOrdinalSuffix(startDay)}, ${startDate.format("HH:mm")}`;
        const endFormat = `${endDate.format("MMM")} ${endDay}${getOrdinalSuffix(endDay)}, ${endDate.format("HH:mm")}`;

        return `${startFormat} ~ ${endFormat}`;
    };

    if (!activeEvent) {
        return null;
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Card
                sx={{
                    background: 'linear-gradient(135deg, #3BA666 0%, #2d8f47 100%)',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    color: 'white',
                    boxShadow: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -20,
                        left: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <GiftIcon sx={{ fontSize: 40, mb: 1, color: 'white' }} />
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 0.5 }}
                    >
                        {activeEvent.name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ mb: 1, opacity: 0.95 }}
                    >
                        Invite {activeEvent.requiredCount} friends and get {activeEvent.rewardMonths} month{activeEvent.rewardMonths !== 1 ? 's' : ''} free!
                    </Typography>
                    {activeEvent.startAt && activeEvent.endAt && (
                        <Typography
                            variant="caption"
                            sx={{ mb: 2, opacity: 0.9, display: 'block' }}
                        >
                            {formatDateRange(activeEvent.startAt, activeEvent.endAt)}
                        </Typography>
                    )}
                    {isAuthenticated ? (
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={() => navigate(`/referral/${activeEvent.id}`)}
                            sx={{
                                backgroundColor: 'white',
                                color: '#3BA666',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                mt: 0.5,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.3s ease',
                                boxShadow: 1
                            }}
                            startIcon={<GiftIcon sx={{ fontSize: 20 }} />}
                        >
                            Go Event
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={async () => {
                                    try {
                                        await AuthService.signup();
                                    } catch (error) {
                                        Logger.error('Error redirecting to signup:', error);
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#3BA666',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    mt: 0.5,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        transform: 'scale(1.05)',
                                    },
                                    transition: 'all 0.3s ease',
                                    boxShadow: 1
                                }}
                                startIcon={<GiftIcon sx={{ fontSize: 20 }} />}
                            >
                                Sign Up
                            </Button>
                            <Typography
                                variant="caption"
                                sx={{ mt: 1, opacity: 0.85, display: 'block', fontStyle: 'italic' }}
                            >
                                After signing up, go to the event page
                            </Typography>
                        </>
                    )}
                </Box>
            </Card>
        </Box>
    );
}

